import React, { useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import Skeleton from 'react-loading-skeleton'
import classnames from 'classnames'

import { FilterIcon } from '@toasttab/buffet-pui-icons'

import {
  AvailableFilters,
  FilterSettings,
  PaginatedMenusSortCriteria
} from 'src/apollo/onlineOrdering'
import { t } from 'src/translations'
import { useIsMobile, ScreenWidth } from 'shared/js/utils/WindowContext'
import { useModal } from 'shared/components/common/modal'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { ButtonType } from 'src/apollo/sites'
import { BrandedButton } from 'shared/components/common/button'

import { SortDropdown, SortButton, SortModal } from './SortDropdown'
import { FilterBar } from './FilterBar'
import { ActiveFilterChips } from './ActiveFilterChips'
import { FilterPanel } from './FilterPanel'
import { orderFilterGroups, OrderedFilterGroup } from './filterSettingsOrder'

type FilterAndSortNavProps = {
  onSortChange:
    | ((sort: PaginatedMenusSortCriteria[] | null) => void)
    | undefined
  currentSort: PaginatedMenusSortCriteria[] | undefined | null
  totalCount?: number
  loading?: boolean
  loadingNewFilterData?: boolean
  availableFilters?: AvailableFilters | null
  mobileOptimized?: boolean
  hideOutOfStockItems?: boolean | null
  // Restaurant-configured filter settings. When set, only the tags/options it
  // lists are shown, in its order; when null, every available tag and option is.
  filterSettings?: FilterSettings | null
}

export const FilterAndSortNav = ({
  onSortChange,
  currentSort,
  totalCount,
  loading,
  loadingNewFilterData,
  availableFilters,
  mobileOptimized,
  hideOutOfStockItems,
  filterSettings
}: FilterAndSortNavProps) => {
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const { track } = useThrottledTracker()
  // Tag/option groups in restaurant-configured order. Null/empty when the store
  // has no enabled filterSettings, in which case no tag/option filters show.
  const orderedFilterGroups = useMemo(
    () => orderFilterGroups(availableFilters, filterSettings),
    [availableFilters, filterSettings]
  )
  const hasFilters = shouldShowFilters(
    availableFilters,
    hideOutOfStockItems,
    orderedFilterGroups
  )

  const debouncedSortChange = useMemo(
    () =>
      debounce((value: PaginatedMenusSortCriteria[] | null) => {
        const criterion = value?.[0] ?? null
        track(TrackingEvents.MENU_SORT_CHANGED, {
          sortField: criterion?.field ?? null,
          sortOrder: criterion?.order ?? null
        })
        onSortChange?.(value)
      }, 300),
    [onSortChange, track]
  )

  useEffect(() => {
    return () => {
      debouncedSortChange.cancel()
    }
  }, [debouncedSortChange])

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)
  const sortModal = useModal()

  // Owns the dropdown track so it stays mounted while FilterBar returns null
  // pre-measure; FilterBar measures this ref to size its dropdowns to the
  // leftover space (see parentRef in FilterBar).
  const filterBarRef = useRef<HTMLDivElement>(null)

  if (loading && !isMobile) {
    return <FilterAndSortNavLoadingSkeleton currentSort={currentSort} />
  }

  if (isMobile) {
    return (
      <>
        <div className='filterSortRow'>
          <BrandedButton
            variant={ButtonType.Text}
            onClick={() => setIsFilterPanelOpen(true)}
            iconRight={<FilterIcon accessibility='decorative' />}
            className='filterButton'
            disabled={loading}
          >
            {t('filters.title')}
          </BrandedButton>
          <SortButton
            onClick={sortModal.onOpen}
            currentSort={currentSort}
            disabled={loading}
          />
        </div>
        <SortModal
          isOpen={sortModal.isOpen}
          onClose={sortModal.onClose}
          onSortChange={debouncedSortChange}
          currentSort={currentSort}
        />
        <div className='paddedContentWrapper'>
          <div
            className={classnames('paddedContent filterSortSection', {
              mobileOptimized
            })}
          >
            <ActiveFilterChips availableFilters={availableFilters} />
            {loading || loadingNewFilterData ? (
              <span className='productCount'>
                <Skeleton
                  width='100px'
                  containerClassName='loadingSkeleton'
                  containerTestId='product-count-skeleton'
                />
              </span>
            ) : (
              totalCount != null && (
                <span className='productCount'>
                  {t(
                    totalCount === 1
                      ? 'filter-and-sort.product-count-singular'
                      : 'filter-and-sort.product-count-plural',
                    { count: totalCount }
                  )}
                </span>
              )
            )}
          </div>
        </div>
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          availableFilters={availableFilters}
          hideOutOfStockItems={hideOutOfStockItems}
          orderedFilterGroups={orderedFilterGroups}
        />
      </>
    )
  }

  return (
    <div className='paddedContentWrapper'>
      <div className='paddedContent filterSortSection'>
        <div className='filterSortRow'>
          <div className='filterBarTrack' ref={filterBarRef}>
            <FilterBar
              availableFilters={availableFilters}
              parentRef={filterBarRef}
              hideOutOfStockItems={hideOutOfStockItems}
              hasFilters={hasFilters}
              orderedFilterGroups={orderedFilterGroups}
            />
          </div>
          {hasFilters && (
            <button
              className='viewAllFiltersButton'
              onClick={() => setIsFilterPanelOpen(true)}
            >
              {t('filters.view-all')}
            </button>
          )}
          <SortDropdown
            onSortChange={debouncedSortChange}
            currentSort={currentSort}
          />
        </div>

        <div className='productCountRow'>
          {loadingNewFilterData ? (
            <span className='productCount'>
              <Skeleton
                width='100px'
                containerClassName='loadingSkeleton'
                containerTestId='product-count-skeleton'
              />
            </span>
          ) : (
            totalCount != null && (
              <span className='productCount'>
                {t(
                  totalCount === 1
                    ? 'filter-and-sort.product-count-singular'
                    : 'filter-and-sort.product-count-plural',
                  { count: totalCount }
                )}
              </span>
            )
          )}
          <ActiveFilterChips availableFilters={availableFilters} />
        </div>
      </div>
      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        availableFilters={availableFilters}
        hideOutOfStockItems={hideOutOfStockItems}
        orderedFilterGroups={orderedFilterGroups}
      />
    </div>
  )
}

const FilterAndSortNavLoadingSkeleton = ({
  currentSort
}: {
  currentSort: PaginatedMenusSortCriteria[] | undefined | null
}) => {
  return (
    <div className='paddedContentWrapper'>
      <div className='paddedContent filterSortSection'>
        <div className='filterSortRow'>
          <Skeleton
            width='150px'
            height='48px'
            containerClassName='loadingSkeleton'
            containerTestId='filter-bar-skeleton'
          />
          <SortButton onClick={() => {}} currentSort={currentSort} disabled />
        </div>
        <div className='productCountRow'>
          <span className='productCount'>
            <Skeleton
              width='100px'
              containerClassName='loadingSkeleton'
              containerTestId='product-count-skeleton'
            />
          </span>
        </div>
      </div>
    </div>
  )
}

// determine if we should display the "view all filters" button + the filter dropdowns
const shouldShowFilters = (
  availableFilters: AvailableFilters | null | undefined,
  hideOutOfStockItems: boolean | null | undefined,
  orderedFilterGroups: OrderedFilterGroup[] | null | undefined
) => {
  const { price } = availableFilters ?? {}
  // Tag/option filters show only when the restaurant has configured (and enabled)
  // filterSettings; orderFilterGroups returns null/[] otherwise.
  const hasTagOrOptionFilters = !!orderedFilterGroups?.length
  // Availability filter is always shown unless out-of-stock items are explicitly hidden. This does not depend on availableFilters being present
  return (
    !hideOutOfStockItems ||
    (!!price && price.min < price.max) ||
    hasTagOrOptionFilters
  )
}
